import { BASELINE_GRACE_DAYS, BASELINE_MAX_LOOKBACK, BASELINE_TARGET_DAYS } from './constants';
import { type Day, isObserved, mean, median } from './day';
import type { CampaignRun } from './wave';

/** A run's demand during it and over its clean baseline (CONTEXT.md, Campaign baseline). */
export type RunMeasurement = {
  readonly avgQsDuring: number | null; // mean daily units over observed, in-stock run days, null units as 0
  readonly avgQsBefore: number | null; // median daily units over the clean baseline days (skips promo, OOS, grace period), null units as 0
  readonly baselineDays: number; // number of active vendor days in baseline window
  readonly baselineInStockRate: number | null; // fraction of baseline days with stock (null = no stock data)
  readonly avgRevDuring: number | null; // mean daily revenue over the run days avgQsDuring counts, null revenue as 0
  readonly avgRevBefore: number | null; // median daily revenue over the clean baseline days
};

/**
 * Scans back from BASELINE_GRACE_DAYS before the run start, at most BASELINE_MAX_LOOKBACK days,
 * for up to BASELINE_TARGET_DAYS observed, non-promotional, in-stock days.
 */
export function measureRun(
  days: readonly Day[],
  run: Pick<CampaignRun, 'startDate' | 'endDate'>
): RunMeasurement {
  const start = days.findIndex((d) => d.date === run.startDate);
  const end = days.findIndex((d) => d.date === run.endDate);

  const qsBefore: number[] = [];
  const revBefore: number[] = [];
  let baselineActiveDays = 0;
  let baselineStockDays = 0;
  let baselineStockDataDays = 0;
  const scanEnd = Math.max(0, start - BASELINE_MAX_LOOKBACK);
  for (let j = start - BASELINE_GRACE_DAYS - 1; j >= scanEnd; j--) {
    if (baselineActiveDays >= BASELINE_TARGET_DAYS) break;
    const day = days[j];
    if (!isObserved(day) || day.kind === 'discount') continue;
    const hasStockData = day.inStock !== null;
    const isOos = day.inStock === false;
    if (hasStockData) {
      baselineStockDataDays++;
      if (!isOos) baselineStockDays++;
    }
    // Out-of-stock demand is censored.
    if (isOos) continue;
    // Units unknown without a stock reading count on neither side (CONTEXT.md, Campaign baseline).
    if (day.unitsSold === null) continue;
    baselineActiveDays++;
    revBefore.push(day.revenue ?? 0);
    qsBefore.push(day.unitsSold);
  }

  const qsDuring: number[] = [];
  const revDuring: number[] = [];
  for (let j = start; j <= end; j++) {
    const day = days[j];
    if (!isObserved(day) || day.inStock === false || day.unitsSold === null) continue;
    qsDuring.push(day.unitsSold);
    revDuring.push(day.revenue ?? 0);
  }

  return {
    avgQsDuring: mean(qsDuring),
    avgQsBefore: median(qsBefore),
    baselineDays: baselineActiveDays,
    baselineInStockRate:
      baselineStockDataDays > 0 ? baselineStockDays / baselineStockDataDays : null,
    avgRevDuring: mean(revDuring),
    avgRevBefore: median(revBefore),
  };
}
